import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Dropdown, DropdownItem } from "flowbite-react";
import { Button } from "@/components/ui/Button/Button";
import { Typography } from "@/components/ui/Typography/Typography";
import {
  useEnhancedRoleList,
  useDeleteRoleMutation,
} from "@/api/role-management/role-management.hooks";
import {
  TRole,
  TEnhancedRole,
} from "@/api/role-management/role-management.types";
import { Eye, Pencil, Trash2, Users, PauseCircle, Plus } from "lucide-react";
import { RoleCard } from "./RoleCard";

import { useTranslation } from "react-i18next";

import ErrorFallback from "@/components/error-fallback/ErrorFallback";
import {
  CommonCardUI,
  CommonDeleteModal,
  CommonNoRecordMessage,
  TooltipComp,
} from "@/components/ui";
import CommonPaginationCard from "@/components/ui/Table/CommonPaginationCard";
import {
  createColumnHelper,
  PaginationState,
  SortingState,
} from "@tanstack/react-table";
import { PAGE_INDEX, PAGE_LIMIT_TEN } from "@/core/const";
import RoleCardLoader from "@/pages/layout/loaders/RoleCardLoader";
import PaginationLoader from "@/pages/layout/loaders/PaginationLoader";

import CommonTable from "@/components/ui/Table/CommonTable";
import CommonPaginationUI from "@/components/ui/Table/CommonPagination";
import TableListingLoaders from "@/pages/layout/loaders/TableListingLoaders";
import FilterPopover, {
  TAppliedFilters,
} from "@/components/ui/Filter/FilterPopover";
import { TStrictColumnDef } from "../../evaluation-list/EvaluationList";
import { usePermissions } from "@/hooks/usePermissions";
import { PermissionGuard } from "@/components/PermissionGuard";
import KpiLoaders from "@/pages/layout/loaders/KpiLoaders";

type TRoleListViewProps = {
  onRoleSelect: (role: TEnhancedRole) => void;
  onAddRole: () => void;
  onEditRole: (role: TEnhancedRole) => void;
  onViewRole: (role: TEnhancedRole) => void;
  selectedRole?: TRole | null;
  /** Optional initial layout; defaults to table. Useful for card-view coverage. */
  initialViewType?: "table" | "card";
};

// Flat filter state — single source of truth, mirrors Services.tsx pattern
type TRoleFilters = {
  status: string;
  searchQuery: string;
  searchType: string;
  permissionMin: string;
  permissionMax: string;
  userMin: string;
  userMax: string;
};

export const RoleListView: React.FC<TRoleListViewProps> = ({
  onRoleSelect,
  onAddRole,
  onEditRole,
  onViewRole,
  selectedRole,
  initialViewType = "table",
}) => {
  const { t } = useTranslation(["roleManagementPage", "common"]);
  const { hasPermission } = usePermissions();

  const getDefaultFilters = useCallback((): TRoleFilters => {
    return {
      status: "active",
      searchQuery: "",
      searchType: "",
      permissionMin: "",
      permissionMax: "",
      userMin: "",
      userMax: "",
    };
  }, []);

  // Single flat filter state — no separate countRange state needed.
  // All filter values live here and feed directly into queryparams.
  const [filters, setFilters] = useState<TRoleFilters>(getDefaultFilters);

  const [viewType] = useState<"table" | "card">(initialViewType);
  const [sorting, setSorting] = useState<SortingState>([]);

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: PAGE_INDEX,
    pageSize: PAGE_LIMIT_TEN,
  });

  const [openDeleteModal, setOpenDeleteModal] = useState({
    isOpen: false,
    roles: {} as TRole | TEnhancedRole,
  });

  const pagination = useMemo(
    () => ({ pageIndex, pageSize }),
    [pageIndex, pageSize],
  );

  // Status options
  const statusOptions = useMemo(
    () => [
      { value: "active", status: t("common:ACTIVE") },
      { value: "inactive", status: t("common:INACTIVE") },
    ],
    [t],
  );

  const columnHelper = createColumnHelper<TEnhancedRole>();

  // Build query params from unified filter state.
  // All filter fields are read from the same `filters` object so changes
  // to any field (search, sort, ranges) automatically trigger a new request.
  const queryparams = useMemo(() => {
    const params: Record<string, any> = {
      search: filters.searchQuery || undefined,
      search_type: filters.searchType || undefined,
      is_active: filters.status === "active",
      page: pageIndex + 1,
      page_size: pageSize,
      // Only send range params when values are present
      permission_count_min: filters.permissionMin
        ? parseInt(filters.permissionMin)
        : undefined,
      permission_count_max: filters.permissionMax
        ? parseInt(filters.permissionMax)
        : undefined,
      user_count_min: filters.userMin ? parseInt(filters.userMin) : undefined,
      user_count_max: filters.userMax ? parseInt(filters.userMax) : undefined,
    };

    if (sorting.length > 0) {
      params.sort_field = sorting[0].id;
      params.sort_order = sorting[0].desc ? "desc" : "asc";
    }

    return params;
  }, [filters, pageIndex, pageSize, sorting]);

  const {
    data: roleListData,
    isLoading,
    isFetching,
    error,
    refetch,
    isError,
  } = useEnhancedRoleList(queryparams, {
    enabled: hasPermission("can-view-role-groups"),
  });

  // Reset to page 0 whenever sort changes so results are consistent
  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0, // reset page on sort change
    }));
  }, [sorting]);

  const deleteRoleMutation = useDeleteRoleMutation();

  const roles = roleListData?.results ?? [];
  const totalCount = roleListData?.count ?? 0;
  const isTableLoading = isLoading || isFetching;
  const pageCount = Math.ceil(totalCount / pageSize);
  const disablePrev = pageIndex === 0;
  const disableNext = pageIndex >= pageCount - 1;

  const columns = useMemo(() => {
    return [
      columnHelper.accessor("name", {
        id: "name",
        header: t("ROLES.TABLE_HEADERS.NAME"),
        cell: ({ getValue, row }) => (
          <div>
            <Typography variant="p" affects="medium" className="text-sm">
              {getValue()}
            </Typography>
            {row.original.description && (
              <Typography
                variant="small"
                className="text-muted-foreground truncate max-w-xs pt-1"
              >
                {row.original.description}
              </Typography>
            )}
          </div>
        ),
        size: 200,
        enableSorting: true,
      }),

      columnHelper.accessor("rank", {
        id: "rank",
        header: t("ROLES.TABLE_HEADERS.HIERARCHY_LEVEL"),
        cell: ({ getValue }) => (
          <Typography
            variant="article"
            className="text-right text-sm text-foreground"
          >
            {getValue() ?? "—"}
          </Typography>
        ),
        meta: {
          thClass: "justify-end w-full",
          excludeFromSearch: true,
        },
        size: 80,
        enableSorting: false,
      }),

      columnHelper.accessor("permission_count", {
        id: "permission_count",
        header: t("ROLES.TABLE_HEADERS.PERMISSION_COUNT"),
        cell: ({ getValue }) => (
          <Typography
            variant="article"
            className="text-right text-sm text-foreground"
          >
            {getValue()}
          </Typography>
        ),
        meta: {
          thClass: "justify-end w-full",
          excludeFromSearch: true,
        },
        size: 120,
        enableSorting: true,
      }),

      columnHelper.accessor("user_count", {
        id: "user_count",
        header: t("ROLES.TABLE_HEADERS.USER_COUNT"),
        cell: ({ getValue }) => (
          <Typography
            variant="article"
            className="text-end text-sm text-foreground"
          >
            {getValue()}
          </Typography>
        ),
        meta: {
          thClass: "justify-end w-full",
          excludeFromSearch: true,
        },
        size: 120,
        enableSorting: true,
      }),

      columnHelper.accessor("is_active", {
        id: "is_active",
        header: t("ROLES.TABLE_HEADERS.STATUS"),
        cell: ({ getValue }) => {
          const isActive = getValue();
          return (
            <span
              className={`inline-flex px-2 py-1 text-xs font-medium rounded-full items-center gap-1 cursor-pointer ${isActive ? "bg-green/11 text-green" : "bg-gray/11 text-gray"}`}
            >
              {isActive ? (
                <span className="status-circle bg-green" />
              ) : (
                <PauseCircle className="w-3 h-3" />
              )}
              <Typography variant="article" className="text-xs">
                {isActive ? t("GLOBAL.ACTIVE") : t("GLOBAL.INACTIVE")}
              </Typography>
            </span>
          );
        },
        meta: {
          thClass: "justify-center text-center w-full",
          tdClass: "text-center",
          excludeFromSearch: true,
        },
        size: 100,
        enableSorting: true,
      }),

      columnHelper.accessor("created_by", {
        id: "created_by",
        header: t("ROLES.TABLE_HEADERS.CREATED_BY"),
        cell: ({ getValue }) => (
          <Typography
            variant="article"
            className="text-sm text-muted-foreground"
          >
            {getValue() ?? "-"}
          </Typography>
        ),
        meta: {
          excludeFromSearch: true,
        },
        size: 120,
        enableSorting: true,
      }),

      columnHelper.display({
        id: "actions",
        header: t("ROLES.TABLE_HEADERS.ACTIONS"),
        cell: ({ row }) => (
          <div className="flex items-center justify-center gap-2">
            <PermissionGuard permissions={["can-view-role-groups"]}>
              <TooltipComp
                content={t("ROLES.TOOLTIP.VIEW")}
                placement="left"
                className="pointer-events-none"
              >
                <Button
                  variant="action"
                  size="icon"
                  className="p-2 rounded-full hover:text-blue-600"
                  onClick={() => onViewRole(row.original)}
                  id={`view-role-button-${row.original.id}`}
                >
                  <Eye size={16} />
                </Button>
              </TooltipComp>
            </PermissionGuard>

            <PermissionGuard permissions={["can-update-role-group"]}>
              <TooltipComp
                content={t("ROLES.TOOLTIP.EDIT")}
                placement="left"
                className="pointer-events-none"
              >
                <Button
                  variant="action"
                  size="icon"
                  className="p-2 rounded-full hover:text-amber-600"
                  onClick={() => onEditRole(row.original)}
                  id={`edit-role-button-${row.original.id}`}
                >
                  <Pencil size={16} />
                </Button>
              </TooltipComp>
            </PermissionGuard>

            <PermissionGuard permissions={["can-delete-role-group"]}>
              <TooltipComp
                content={t("ROLES.TOOLTIP.DELETE")}
                placement="left"
                className="pointer-events-none"
              >
                <Button
                  variant="action"
                  size="icon"
                  className="p-2 rounded-full hover:text-destructive"
                  onClick={() =>
                    setOpenDeleteModal({ isOpen: true, roles: row.original })
                  }
                  disabled={deleteRoleMutation.isPending}
                  id={`delete-role-button-${row.original.id}`}
                >
                  <Trash2 size={16} />
                </Button>
              </TooltipComp>
            </PermissionGuard>

            <PermissionGuard permissions={["can-assign-permissions"]}>
              <TooltipComp
                content={t("ROLES.TOOLTIP.ASSIGN_PERMISSIONS")}
                placement="left"
                className="pointer-events-none"
              >
                <Button
                  variant="action"
                  size="icon"
                  className="p-2 rounded-full hover:text-destructive"
                  onClick={() => onRoleSelect(row.original)}
                  id={`assign-permissions-button-${row.original.id}`}
                >
                  <Users size={16} />
                </Button>
              </TooltipComp>
            </PermissionGuard>
          </div>
        ),
        meta: {
          type: "action",
          thClass: "justify-center w-full",
          excludeFromSearch: true,
        },
        size: 120,
        enableSorting: false,
      }),
    ] as TStrictColumnDef<TEnhancedRole>[];
  }, [
    columnHelper,
    t,
    deleteRoleMutation.isPending,
    onViewRole,
    onEditRole,
    onRoleSelect,
  ]);

  // Apply filters from FilterPopover — mirrors Services.tsx handleApplyFilter pattern.
  // All fields come from the callback payload; no stale closure reads.
  const handleApplyFilters = useCallback((applied: TAppliedFilters) => {
    setFilters((prev) => ({
      ...prev,
      status: typeof applied.status === "string" ? applied.status : prev.status,
      searchQuery: applied.searchQuery ?? "",
      searchType: applied.searchType ?? "",
      permissionMin: applied.permissionCountMin ?? "",
      permissionMax: applied.permissionCountMax ?? "",
      userMin: applied.userCountMin ?? "",
      userMax: applied.userCountMax ?? "",
    }));
    // Reset to page 0 whenever filters change
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(getDefaultFilters());
    setPagination({ pageIndex: 0, pageSize });
  }, [getDefaultFilters, pageSize]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      setPagination({ pageIndex: newPage, pageSize });
    },
    [pageSize],
  );

  const handleRowsPerPageChange = useCallback((newPageSize: number) => {
    setPagination({ pageIndex: 0, pageSize: newPageSize });
  }, []);

  const handleDelete = async (role: TRole | TEnhancedRole) => {
    if (!hasPermission("can-delete-role-group")) return;
    try {
      await deleteRoleMutation.mutateAsync(role.internal_id);
      await refetch();
      setOpenDeleteModal((prev) => ({ ...prev, isOpen: false }));
    } catch (error) {
      console.log(error);
    }
  };

  // Compute whether any non-default filter is active for the card empty state
  const hasActiveFilters = useMemo(() => {
    const defaults = getDefaultFilters();
    return (
      filters.status !== defaults.status ||
      filters.searchQuery !== "" ||
      filters.permissionMin !== "" ||
      filters.permissionMax !== "" ||
      filters.userMin !== "" ||
      filters.userMax !== ""
    );
  }, [filters, getDefaultFilters]);

  return (
    <ErrorFallback isError={isError} error={error ?? undefined}>
      <div className="space-y-3">
        {/* ─── Table View ─── */}
        {viewType === "table" && (
          <CommonCardUI
            className="custom-bx-shadow bg-background"
            classNameContent="p-0"
          >
            <div className="flex items-center justify-between border-b border-dashed p-3">
              <div>
                <FilterPopover
                  component="role-management"
                  defaultFilters={getDefaultFilters()}
                  initialFilters={{
                    status: filters.status,
                    searchQuery: filters.searchQuery,
                    searchType: filters.searchType,
                    permissionCountMin: filters.permissionMin,
                    permissionCountMax: filters.permissionMax,
                    userCountMin: filters.userMin,
                    userCountMax: filters.userMax,
                  }}
                  id="role-management-filter-popover"
                  onApplyFilters={handleApplyFilters}
                  onClearFilters={clearFilters}
                  statusData={{ data: statusOptions }}
                  tableColumns={columns}
                />
              </div>
              <PermissionGuard permissions={["can-create-role-group"]}>
                <Button
                  size="sm"
                  variant="default"
                  type="button"
                  onClick={onAddRole}
                  className="gap-2 font-medium"
                >
                  {/* Add New Role */}
                  <Plus size={16} /> {t("ROLES.BUTTONS.ADD_NEW_ROLE")}
                </Button>
              </PermissionGuard>
            </div>

            <div className="p-3">
              {roles.length === 0 && !isTableLoading ? (
                <div className="text-center py-8">
                  <CommonNoRecordMessage msg={t("ROLES.NO_ROLES_FOUND")} />
                </div>
              ) : (
                <ErrorFallback isError={isError} error={error ?? undefined}>
                  {!isTableLoading && (
                    <>
                      <div className="w-full overflow-x-auto">
                        <CommonTable
                          columns={columns}
                          rows={roles}
                          totalRecordCount={totalCount}
                          setSorting={setSorting}
                          sorting={sorting}
                          pagination={pagination}
                          setPagination={setPagination}
                          manualSorting={true}
                        />
                      </div>
                      <CommonPaginationUI
                        totalRecordCount={totalCount}
                        page={pageIndex}
                        pageCount={pageCount}
                        rowsPerPage={pageSize}
                        onPageChange={handlePageChange}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        disablePrev={disablePrev}
                        disableNext={disableNext}
                      />
                    </>
                  )}
                </ErrorFallback>
              )}
            </div>

            {isTableLoading && <TableListingLoaders />}
          </CommonCardUI>
        )}

        {/* ─── Card View ─── */}
        {viewType === "card" && (
          <>
            <div className="flex items-center justify-between">
              <FilterPopover
                component="role-management"
                defaultFilters={getDefaultFilters()}
                initialFilters={{
                  status: filters.status,
                  searchQuery: filters.searchQuery,
                  searchType: filters.searchType,
                  permissionCountMin: filters.permissionMin,
                  permissionCountMax: filters.permissionMax,
                  userCountMin: filters.userMin,
                  userCountMax: filters.userMax,
                }}
                id="role-management-filter-popover"
                onApplyFilters={handleApplyFilters}
                onClearFilters={clearFilters}
                statusData={{ data: statusOptions }}
                tableColumns={columns}
              />
            </div>

            {isLoading ? (
              <KpiLoaders numberOfLoaders={1} className="h-6 w-24" />
            ) : (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Typography variant="article" className="text-foreground">
                    {t("common:PAGINATION.TOTAL_RECORDS")}:{" "}
                    <Typography
                      variant="article"
                      affects="semiBold"
                      className="shadow-sm bg-white dark:bg-gray-800 rounded-full px-2 py-1 text-primary"
                    >
                      {totalCount ?? 0}
                    </Typography>
                  </Typography>
                </div>
              </div>
            )}

            {isLoading && (
              <RoleCardLoader numberOfLoaders={8} className="h-48" />
            )}

            {!isLoading && (
              <>
                {roles.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-6">
                    {roles.map((role, index) => (
                      <RoleCard
                        key={`role-${role.internal_id}-${index}`}
                        role={role}
                        isSelected={
                          selectedRole?.internal_id === role.internal_id
                        }
                        onSelect={
                          onRoleSelect as (role: TRole | TEnhancedRole) => void
                        }
                        onEdit={
                          onEditRole as (role: TRole | TEnhancedRole) => void
                        }
                        onView={
                          onViewRole as (role: TRole | TEnhancedRole) => void
                        }
                        onDelete={(role) =>
                          setOpenDeleteModal({ isOpen: true, roles: role })
                        }
                      />
                    ))}
                  </div>
                ) : (
                  <div className="border dark:border-gray-700 text-center py-12 rounded-lg shadow">
                    <div className="mx-auto w-24 h-24 border border-gray-200 dark:border-gray-700 rounded-full flex items-center justify-center mb-4">
                      <Users className="w-12 h-12 text-muted-foreground" />
                    </div>
                    <Typography
                      variant="h4"
                      className="text-muted-foreground mb-2"
                    >
                      {hasActiveFilters
                        ? t("ROLES.NO_ROLES_MATCH_FILTERS")
                        : t("ROLES.NO_ROLES_FOUND")}
                    </Typography>
                    <Typography
                      variant="p"
                      affects="muted"
                      className="mb-6 max-w-md mx-auto dark:text-muted-foreground"
                    >
                      {hasActiveFilters
                        ? t("PERMISSION_ASSIGNMENT.TRY_ADJUSTING_FILTERS")
                        : t("ROLES.GET_STARTED")}
                    </Typography>
                    {hasActiveFilters ? (
                      <Button
                        variant="outline"
                        onClick={clearFilters}
                        id="clear-filters-button"
                      >
                        {t("GLOBAL.CLEAR_FILTERS")}
                      </Button>
                    ) : (
                      <Button onClick={onAddRole} id="create-first-role-button">
                        {t("ROLES.BUTTONS.CREATE_FIRST_ROLE")}
                      </Button>
                    )}
                  </div>
                )}
              </>
            )}

            {isLoading ? (
              <PaginationLoader />
            ) : (
              <>
                {roles.length > 0 && (
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2 mb-4 dropdown-main-section">
                      <Typography
                        variant="small"
                        className="text-muted-foreground"
                      >
                        {t("GLOBAL.SHOW")}:
                      </Typography>
                      <Dropdown
                        label={pageSize.toString()}
                        dismissOnClick={true}
                        size="default"
                        className="w-20 flex items-center justify-between rounded border border-gray-300 bg-background px-2 py-1 text-sm text-foreground hover:border-gray-400 dark:border-gray-700"
                      >
                        {[10, 20, 50, 100].map((size) => (
                          <DropdownItem
                            key={size}
                            className="hover:bg-slate-200"
                            onClick={() =>
                              setPagination({ pageIndex: 0, pageSize: size })
                            }
                          >
                            {size}
                          </DropdownItem>
                        ))}
                      </Dropdown>
                    </div>

                    <div className="mt-auto pt-4">
                      <CommonPaginationCard
                        totalRecordCount={totalCount}
                        page={pageIndex}
                        pageCount={pageCount}
                        rowsPerPage={pageSize}
                        onPageChange={handlePageChange}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        disablePrev={disablePrev}
                        disableNext={disableNext}
                        id="role-list-pagination-card"
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* ─── Delete Modal ─── */}
        {openDeleteModal.isOpen && (
          <div data-testid="delete-modal">
            <CommonDeleteModal
              open={openDeleteModal.isOpen}
              onClose={() =>
                setOpenDeleteModal((prev) => ({ ...prev, isOpen: false }))
              }
              buttonAccept={() => {
                void handleDelete(openDeleteModal.roles);
              }}
              buttonCancel={() =>
                setOpenDeleteModal((prev) => ({ ...prev, isOpen: false }))
              }
              title={t("ROLES.DELETE_CONFIRMATION", {
                name: openDeleteModal.roles.name,
              })}
              size="sm"
              loading={deleteRoleMutation.isPending}
              id="role-delete-modal"
            />
          </div>
        )}
      </div>
    </ErrorFallback>
  );
};
